import { useMemo, useState } from "react";
import {
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Grid,
} from "@mui/material";
import {
  MdWarning,
  MdCalendarToday,
  MdPerson,
  MdCreditCard,
  MdInfo,
  MdArrowBack,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ErrorRetry } from "../../components/common/ErrorRetry";
import { getErrorMessage } from "../../utils/error";
import {
  useAccountSubscriptionDetail,
  useSubscriptionTransactions,
} from "../../hooks/useSubscribtionDashboard";
import getAccountById from "../../controllers/user/getAccountById";

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-CA");
};

const formatAmount = (amount, currency = "NGN") => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return "-";
  const symbol = currency === "NGN" ? "₦" : `${currency} `;
  return `${symbol}${numeric.toLocaleString()}`;
};

const getStatusColor = (status) => {
  const normalized = `${status || ""}`.toLowerCase();

  if (["active", "paid", "success", "completed"].includes(normalized)) {
    return { bg: "#DCFCE7", color: "#16A34A" };
  }

  if (["trial", "pending", "processing"].includes(normalized)) {
    return { bg: "#DBEAFE", color: "#1F4FFF" };
  }

  if (["grace period", "grace", "overdue"].includes(normalized)) {
    return { bg: "#FEF3C7", color: "#F59E0B" };
  }

  if (
    ["inactive", "failed", "cancelled", "canceled", "expired"].includes(
      normalized,
    )
  ) {
    return { bg: "#FEE2E2", color: "#DC2626" };
  }

  return { bg: "#F1F5F9", color: "#64748B" };
};

const getDisplayName = (user = {}) => {
  const firstName = user?.firstName ?? user?.first_name;
  const lastName = user?.lastName ?? user?.last_name;
  return (
    [firstName, lastName].filter(Boolean).join(" ") ||
    user?.name ||
    "Unknown User"
  );
};

const getDisplayEmail = (user = {}) => user?.email || "-";

const getPlanName = (detail = {}) => detail?.plan?.name;

const getPlanColor = (detail = {}) => detail?.plan?.color ?? "#1F4FFF";

const getSubscriptionStatus = (subscriptionDetail) => {
  return subscriptionDetail?.isSubscriptionActive ? "Active" : "Inactive";
};

const getGracePeriodEnd = (detail = {}) =>
  detail?.graceEndsAt ?? detail?.gracePeriodEndsAt ?? detail?.graceEndDate;

const matchesAccount = (transaction, userId, email) => {
  const account = transaction?.account || {};
  return (
    account?._id === userId ||
    account?.id === userId ||
    transaction?.accountId === userId ||
    (email && account?.email === email)
  );
};

const EmptyState = ({ message }) => (
  <Typography className="text-slate-600 py-8 text-center">{message}</Typography>
);

const InfoCard = ({ label, value }) => (
  <Box
    sx={{
      border: "1px solid #E2E8F0",
      borderRadius: "14px",
      p: 2,
      bgcolor: "#FFFFFF",
      height: "100%",
    }}
  >
    <Typography variant="body2" className="text-slate-500 mb-1">
      {label}
    </Typography>
    <Typography className="font-semibold text-slate-900 break-words">
      {value || "-"}
    </Typography>
  </Box>
);

export default function UserSubscriptionDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  const {
    data: subscriptionDetailData,
    isLoading: subscriptionLoading,
    isError: subscriptionError,
    error: subscriptionErrorObj,
    refetch: refetchSubscription,
  } = useAccountSubscriptionDetail(userId);

  const {
    data: accountData,
    isLoading: accountLoading,
    isError: accountError,
    error: accountErrorObj,
    refetch: refetchAccount,
  } = useQuery({
    queryKey: ["subscriptionDashboardAccount", userId],
    queryFn: async () => {
      const response = await getAccountById(userId);
      if (!response?.return) {
        throw new Error(
          response?.msg || "Something went wrong fetching account",
        );
      }
      return response?.data;
    },
    enabled: Boolean(userId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const {
    data: transactionsData,
    isLoading: transactionsLoading,
    isError: transactionsError,
    error: transactionsErrorObj,
    refetch: refetchTransactions,
  } = useSubscriptionTransactions({
    limit: 100,
    offset: 0,
  });

  const subscriptionDetail = useMemo(() => {
    return (
      subscriptionDetailData?.data?.data ??
      subscriptionDetailData?.data ??
      subscriptionDetailData ??
      {}
    );
  }, [subscriptionDetailData]);

  const account = useMemo(() => {
    return (
      accountData ??
      subscriptionDetail?.account ??
      subscriptionDetail?.user ??
      {}
    );
  }, [accountData, subscriptionDetail]);

  const transactions = useMemo(() => {
    const allTransactions =
      transactionsData?.data?.data ??
      transactionsData?.data ??
      transactionsData?.data?.transactions ??
      [];

    const email = getDisplayEmail(account);
    return allTransactions.filter((transaction) =>
      matchesAccount(transaction, userId, email),
    );
  }, [account, transactionsData, userId]);

  const subscriptionActivity = useMemo(() => {
    const rows = [];
    const planName = getPlanName(subscriptionDetail);
    const status = getSubscriptionStatus(subscriptionDetail);

    if (subscriptionDetail && Object.keys(subscriptionDetail).length > 0) {
      rows.push({
        id:
          subscriptionDetail?._id ||
          subscriptionDetail?.id ||
          "current-subscription",
        action: "Current Subscription",
        from: subscriptionDetail?.previousPlan?.name || "-",
        to: planName,
        date: formatDate(
          subscriptionDetail?.updatedAt ||
            subscriptionDetail?.createdAt ||
            account?.createdAt,
        ),
        amount: formatAmount(
          subscriptionDetail?.amount ??
            subscriptionDetail?.plan?.price ??
            subscriptionDetail?.plan?.monthly ??
            subscriptionDetail?.plan?.annual,
          subscriptionDetail?.currency,
        ),
        status,
      });
    }

    transactions.forEach((transaction) => {
      rows.push({
        id: transaction?._id || transaction?.id,
        action: transaction?.reason || "Subscription",
        from:
          transaction?.meta?.previousPlan || transaction?.meta?.fromPlan || "-",
        to: transaction?.description || transaction?.meta?.planName || planName,
        date: formatDate(transaction?.createdAt),
        amount: formatAmount(transaction?.amount, transaction?.currency),
        status: transaction?.status || "-",
      });
    });

    return rows;
  }, [account, subscriptionDetail, transactions]);

  const trialHistory = useMemo(() => {
    const status = `${getSubscriptionStatus(subscriptionDetail)}`.toLowerCase();
    const isTrial =
      status === "trial" ||
      subscriptionDetail?.plan?.mode === "TRIAL" ||
      subscriptionDetail?.isTrial;

    if (!isTrial) return [];

    return [
      {
        id: subscriptionDetail?._id || "trial",
        feature: getPlanName(subscriptionDetail),
        startDate: formatDate(
          subscriptionDetail?.createdAt || account?.createdAt,
        ),
        endDate: formatDate(subscriptionDetail?.nextChargingDate),
        status: subscriptionDetail?.converted ? "Converted" : "Active Trial",
      },
    ];
  }, [account, subscriptionDetail]);

  const accountOverview = useMemo(
    () => [
      { label: "User ID", value: userId },
      { label: "User Type", value: account?.userType || "-" },
      { label: "Account Status", value: account?.accountStatus || "-" },
      { label: "Joined", value: formatDate(account?.createdAt) },
      { label: "Phone", value: account?.phone || "-" },
      {
        label: "Plan Mode",
        value:
          subscriptionDetail?.plan?.mode ||
          subscriptionDetail?.plan?.name ||
          "-",
      },
    ],
    [account, subscriptionDetail, userId],
  );

  const displayName = getDisplayName(account);
  const displayEmail = getDisplayEmail(account);
  const planName = getPlanName(subscriptionDetail);
  const planColor = getPlanColor(subscriptionDetail);
  const subscriptionStatus = getSubscriptionStatus(subscriptionDetail);
  const expiryDate = formatDate(subscriptionDetail?.nextChargingDate);
  const joinedDate = formatDate(
    account?.createdAt || subscriptionDetail?.createdAt,
  );
  const graceEndsAt = formatDate(getGracePeriodEnd(subscriptionDetail));
  const isGracePeriod = ["grace period", "grace"].includes(
    `${subscriptionStatus}`.toLowerCase(),
  );
  const statusColor = getStatusColor(subscriptionStatus);
  const isLoading = subscriptionLoading || accountLoading;
  const hasError =
    accountError ||
    (subscriptionError &&
      !accountData &&
      !subscriptionDetail?.account &&
      !subscriptionDetail?.user);
  const errorMessage = getErrorMessage(accountErrorObj || subscriptionErrorObj);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (hasError) {
    return (
      <ErrorRetry
        message={errorMessage}
        onRetry={() => {
          refetchSubscription();
          refetchAccount();
        }}
      />
    );
  }

  return (
    <Box>
      <Box
        className="inline-flex items-center gap-2 mb-4 cursor-pointer text-slate-700 hover:text-[#1F4FFF] transition-colors"
        onClick={() => navigate("/subscritionDashboard/users")}
      >
        <MdArrowBack size={20} />
        <Typography className="font-medium">Back to users</Typography>
      </Box>

      {/* User Profile Header */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Box className="flex items-center gap-4">
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: planColor,
                  fontSize: "2rem",
                }}
              >
                {displayName.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h5" className="font-bold text-slate-900">
                  {isLoading ? "Loading user..." : displayName}
                </Typography>
                <Typography variant="body2" className="text-slate-600 mb-2">
                  {displayEmail}
                </Typography>
                <Box className="flex items-center gap-2">
                  <Chip
                    label={planName}
                    size="small"
                    sx={{
                      bgcolor: planColor,
                      color: "white",
                      fontWeight: 600,
                    }}
                  />
                  <Chip
                    label={subscriptionStatus}
                    size="small"
                    sx={{
                      bgcolor: statusColor.bg,
                      color: statusColor.color,
                      fontWeight: 600,
                    }}
                  />
                  <Typography variant="body2" className="text-slate-500">
                    Expires: {expiryDate}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box className="text-left md:text-right">
              <Typography variant="body2" className="text-slate-500">
                Joined
              </Typography>
              <Typography className="font-semibold text-slate-900">
                {joinedDate}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={3}>
              <InfoCard
                label="Subscription ID"
                value={subscriptionDetail?._id || subscriptionDetail?.id || "-"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoCard label="User Type" value={account?.userType || "-"} />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoCard
                label="Account Status"
                value={account?.accountStatus || "-"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <InfoCard
                label="Billing Period"
                value={
                  subscriptionDetail?.plan?.durationInDays
                    ? `${subscriptionDetail.plan.durationInDays} Days`
                    : "-"
                }
              />
            </Grid>
          </Grid>

          {isGracePeriod && (
            <Alert
              severity="warning"
              icon={<MdWarning />}
              sx={{ mt: 3, borderRadius: "10px" }}
            >
              <Typography variant="body2" className="font-semibold">
                Grace Period Active
              </Typography>
              <Typography variant="body2">
                User&apos;s subscription expired but they&apos;re in a grace
                period until {graceEndsAt}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: "1px solid #E2E8F0",
            px: 2,
          }}
        >
          <Tab
            label="Subscription"
            icon={<MdPerson />}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            label="Billing"
            icon={<MdCreditCard />}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
          <Tab
            label="Account"
            icon={<MdCalendarToday />}
            iconPosition="start"
            sx={{ textTransform: "none", fontWeight: 600 }}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Subscription Tab */}
          {currentTab === 0 && (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>From</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>To</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subscriptionActivity.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <EmptyState message="No subscription activity found for this user." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    subscriptionActivity.map((item) => {
                      const chipColor = getStatusColor(item.status);
                      return (
                        <TableRow key={item.id}>
                          <TableCell>{item.action}</TableCell>
                          <TableCell>{item.from}</TableCell>
                          <TableCell>{item.to}</TableCell>
                          <TableCell>{item.date}</TableCell>
                          <TableCell className="font-semibold">
                            {item.amount}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.status}
                              size="small"
                              sx={{
                                bgcolor: chipColor.bg,
                                color: chipColor.color,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Billing Tab */}
          {currentTab === 1 && (
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: "#F8FAFC" }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Method</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactionsLoading ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <EmptyState message="Loading billing history..." />
                      </TableCell>
                    </TableRow>
                  ) : transactionsError ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <ErrorRetry
                          message={getErrorMessage(transactionsErrorObj)}
                          onRetry={() => refetchTransactions()}
                        />
                      </TableCell>
                    </TableRow>
                  ) : transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <EmptyState message="No billing history found for this user." />
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map((item) => {
                      const chipColor = getStatusColor(item.status);
                      return (
                        <TableRow key={item._id || item.id}>
                          <TableCell>{formatDate(item.createdAt)}</TableCell>
                          <TableCell>{item.reason || "Subscription"}</TableCell>
                          <TableCell className="font-semibold">
                            {formatAmount(item.amount, item.currency)}
                          </TableCell>
                          <TableCell>{item.paymentMode || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.status || "-"}
                              size="small"
                              sx={{
                                bgcolor: chipColor.bg,
                                color: chipColor.color,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Account Tab */}
          {currentTab === 2 && (
            <Box>
              <Box className="flex items-center gap-2 mb-4 text-slate-700">
                <MdInfo />
                <Typography className="font-semibold">
                  Account details
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {accountOverview.map((item) => (
                  <Grid item xs={12} md={6} key={item.label}>
                    <InfoCard label={item.label} value={item.value} />
                  </Grid>
                ))}
                {trialHistory.length > 0 && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #E2E8F0",
                        borderRadius: "14px",
                        p: 2,
                      }}
                    >
                      <Typography className="font-semibold text-slate-900 mb-3">
                        Trial Information
                      </Typography>
                      {trialHistory.map((item) => (
                        <Box
                          key={item.id}
                          className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-2"
                        >
                          <Box>
                            <Typography className="font-medium text-slate-900">
                              {item.feature}
                            </Typography>
                            <Typography
                              variant="body2"
                              className="text-slate-500"
                            >
                              {item.startDate} to {item.endDate}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.status}
                            size="small"
                            sx={{
                              bgcolor: "#DBEAFE",
                              color: "#1F4FFF",
                              fontWeight: 600,
                            }}
                          />
                        </Box>
                      ))}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
